import type { Vec3 } from '../math/vec3'
import { FACE_LM } from '../tracking/landmarks'
import type { FaceAnchorOrigin } from '../product/schema'
import { canonicalVertex } from '../data/canonical-face'

/**
 * Face anchor points in MediaPipe's *canonical face model* space (cm):
 * origin ≈ head centre at eye height, +X = subject's left, +Y = up, +Z = out of the face.
 *
 * `point` is used for depth (via the head matrix); `landmark` is the detected 2-D landmark the
 * screen position is snapped to, so the anchor follows the observed face exactly in x/y.
 * Points on the mesh come straight from the canonical model; off-mesh points (head top, neck) are estimates.
 */
export interface FaceAnchorDef {
  point: Vec3
  landmark: number | null
  /** Optional local rotation (Euler XYZ, deg) of the anchor frame relative to the head frame. */
  rotation?: Vec3
}

const v = canonicalVertex

export const FACE_ANCHORS: Record<FaceAnchorOrigin, FaceAnchorDef> = {
  head_center: { point: [0, 0, 0], landmark: null },
  nose_bridge: { point: v(FACE_LM.NOSE_BRIDGE), landmark: FACE_LM.NOSE_BRIDGE }, // (0, 3.27, 5.24)
  nose_tip: { point: v(FACE_LM.NOSE_TIP), landmark: FACE_LM.NOSE_TIP }, // (0, -1.13, 7.48)
  nostril_left: { point: v(FACE_LM.LEFT_NOSTRIL), landmark: FACE_LM.LEFT_NOSTRIL },
  nostril_right: { point: v(FACE_LM.RIGHT_NOSTRIL), landmark: FACE_LM.RIGHT_NOSTRIL },
  glabella: { point: v(FACE_LM.GLABELLA), landmark: FACE_LM.GLABELLA },
  forehead: { point: v(FACE_LM.FOREHEAD), landmark: FACE_LM.FOREHEAD }, // (0, 8.26, 4.48)
  chin: { point: v(FACE_LM.CHIN), landmark: FACE_LM.CHIN }, // (0, -9.40, 4.26)
  head_top: { point: [0, 11.8, 0.8], landmark: null },
  ear_left: { point: v(FACE_LM.LEFT_EAR), landmark: FACE_LM.LEFT_EAR }, // (7.66, 0.67, -2.44)
  ear_right: { point: v(FACE_LM.RIGHT_EAR), landmark: FACE_LM.RIGHT_EAR },
  /**
   * The face mesh has NO ear geometry — landmarks 132/361 lie on the jaw silhouette in front of
   * the ear, so 2-D refinement would pin earrings to the visible face edge (wrong position, and
   * the far-side earring could never go behind the head). Lobes are therefore anchored purely by
   * the head matrix at an anatomical point: ~2.3 cm below and ~0.6 cm behind the tragus (7.66, 0.67, -2.44).
   */
  ear_lobe_left: { point: [7.35, -1.9, -3.6], landmark: null },
  ear_lobe_right: { point: [-7.35, -1.9, -3.6], landmark: null },
  neck: { point: [0, -16.5, -0.5], landmark: null }, // suprasternal notch (head-only estimate)
}

/**
 * Approximate skull ellipsoid in canonical space (head occluder). Kept *behind* the eye surface
 * (eyes at z ≈ 3.2) and inside the temples' path so it only hides things behind the head.
 */
export const HEAD_ELLIPSOID = {
  center: [0, 0.3, -3.6] as Vec3,
  radii: [7.3, 10.6, 7.4] as Vec3,
}

/** Ear ellipsoids (canonical space) — hide temple tips / earring backs behind the ears. */
/**
 * Kept above the lobe anchor (7.6, -1.7, -3.1): the ellipsoid bottom is ~y -1.6, so a worn earring
 * hangs below it and is not swallowed; it still hides temple tips hooking behind the ear top.
 */
export const EAR_ELLIPSOIDS = {
  left: { center: [8.5, 1.3, -3.9] as Vec3, radii: [1.3, 2.9, 2.4] as Vec3 },
  right: { center: [-8.5, 1.3, -3.9] as Vec3, radii: [1.3, 2.9, 2.4] as Vec3 },
}

/** Neck cylinder (canonical space) — hides the back half of necklace chains. Axis along Y. */
export const NECK_CYLINDER = {
  center: [0, -15.0, -2.0] as Vec3,
  radius: 5.6,
  height: 12,
}
